import { Caption, Stack } from '@eluve/components';

import { TemplateRow } from './TemplateRow';
import type { DocumentTemplate } from './useDocumentTemplates';

export type TemplateSectionProps = {
  title: string;
  templates: DocumentTemplate[];
  isGenerating: boolean;
  onSelect: (t: DocumentTemplate) => void;
  onEdit: (t: DocumentTemplate) => void;
  onDelete: (t: DocumentTemplate) => void;
  /** 'card' wraps the group in a bordered card with a header bar (doc/letter flow). */
  variant?: 'plain' | 'card';
  /** Leading badge shown in the card header (card variant only). */
  badge?: React.ReactNode;
  /** Trailing pill shown in the card header, e.g. "Prebuilt" (card variant only). */
  tag?: React.ReactNode;
};

export const TemplateSection: React.FC<TemplateSectionProps> = ({
  title,
  templates,
  isGenerating,
  onSelect,
  onEdit,
  onDelete,
  variant = 'plain',
  badge,
  tag,
}) => {
  if (templates.length === 0) return null;

  const rows = templates.map((template) => (
    <TemplateRow
      key={template.id}
      template={template}
      isGenerating={isGenerating}
      onSelect={onSelect}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  ));

  if (variant === 'card') {
    return (
      <Stack
        gap={0}
        className="overflow-hidden rounded-xl border border-border-primary"
      >
        <Stack
          direction="horizontal"
          align="center"
          gap={2}
          className="bg-bg-secondary px-3 py-2"
        >
          {badge}
          <Caption inline size="2xs" weight="bold" color="tertiary">
            {title}
          </Caption>
          {tag && <div className="ml-auto">{tag}</div>}
        </Stack>
        <Stack gap={0} className="p-1.5">
          {rows}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={1}>
      <Caption
        inline
        size="2xs"
        weight="bold"
        color="tertiary"
        className="px-2 pt-2"
      >
        {title}
      </Caption>
      {rows}
    </Stack>
  );
};
